//  missed.js
//  Recurring series whose expected charge hasn't arrived.
//
//  Every projector steps a series forward from its last instance, so a charge
//  that never posts just drops out of view: the next cycle appears, and the one
//  that was due is silently gone. That hides three different situations the
//  user should resolve, each with its own answer:
//    - it did post, under a descriptor that didn't match  → link it
//    - it was skipped this cycle                            → skip
//    - the series is over                                   → it ended
//  Answers live at users/{uid}/seriesLifecycle/{firebaseSafeKey(seriesKey)}:
//    { seriesKey, skips: {'YYYY-MM-DD': true}, endedAt: 'YYYY-MM-DD',
//      aliases: {safeNaturalKey: true}, dismissedTxIds: {txId: true} }
//
//  Until one is given, an outflow that's due counts as landing tomorrow on the
//  runway: it's still owed as far as anyone knows, and dropping it would let
//  safe-to-spend read high.

import { recurringSeries } from './runway.js';
import { nextOccurrence } from './recurrence.js';
import { seriesKeyOf } from './series-key.js';
import { parseDay } from './planned.js';
import { naturalSeriesKeyOf } from './series-aliases.js';
import { isMoneyIn } from '../txType.js';
import { firebaseSafeKey } from '../firebaseKey.js';

// Days past the expected date before the user is asked. Posting lags, weekends
// and billing drift all land a real charge a few days late, more so on long
// cycles; asking the moment a charge is due would nag about ones in flight.
const GRACE_DAYS = { daily: 2, weekly: 3, biweekly: 4, monthly: 5, semiannual: 14, yearly: 21 };

// Past two whole cycles, a missed charge is more likely a series that quietly
// ended than one about to post. It's still asked about, but no longer counted.
const CYCLE_DAYS = { daily: 1, weekly: 7, biweekly: 14, monthly: 30, semiannual: 182, yearly: 365 };

// Where a re-described charge may have landed around the expected date, and
// how far its amount may stray. Wider than auto-attach: the user confirms.
const LINK_WINDOW_DAYS = { daily: 1, weekly: 3, biweekly: 5, monthly: 10, semiannual: 21, yearly: 30 };
const LINK_LOW = 0.5;
const LINK_HIGH = 2;
const MAX_CANDIDATES = 3;

const DAY_MS = 24 * 60 * 60 * 1000;
const dayKey = (d) => d.toLocaleDateString('en-CA');
const daysBetween = (a, b) => Math.round((b - a) / DAY_MS);
const round2 = (n) => parseFloat(n.toFixed(2));

export const lifecycleOf = (lifecycle, key) => (lifecycle && lifecycle[firebaseSafeKey(key)]) || null;

/**
 * "It ended" stores the date of the series' last charge at the time. A charge
 * after that date means the series came back, and it counts as live again.
 */
export function isEnded(entry, lastDate) {
  const ended = parseDay(entry?.endedAt);
  const last = parseDay(lastDate);
  return !!ended && !!last && last <= ended;
}

/** Keys of the series the user has marked ended. */
export function endedSeriesKeys(transactions = [], lifecycle = {}) {
  const out = new Set();
  recurringSeries(transactions).forEach((s) => {
    if (isEnded(lifecycleOf(lifecycle, s.key), s.lastDate)) out.add(s.key);
  });
  return out;
}

/**
 * Every live series whose next expected charge (after skips) is due or past.
 *
 * `prompt` — past the grace window; ask the user about it.
 * `stale`  — more than two cycles late; asked about, but not counted.
 */
export function missedCharges(transactions = [], lifecycle = {}, { today = new Date(), excludeSeriesKeys = null } = {}) {
  const start = parseDay(today);

  // The latest instance's category, so a linked charge can inherit it.
  const latest = {};
  transactions.forEach((t) => {
    if (!t || !t.isRecurring) return;
    const d = parseDay(t.date);
    if (!d) return;
    const key = seriesKeyOf(t);
    if (!latest[key] || d >= latest[key].date) latest[key] = { date: d, category: t.category || null };
  });

  return recurringSeries(transactions).flatMap((s) => {
    if (!s.recurrenceType || !(s.amount > 0)) return [];
    if (excludeSeriesKeys && excludeSeriesKeys.has(s.key)) return [];
    const entry = lifecycleOf(lifecycle, s.key);
    const last = parseDay(s.lastDate);
    if (isEnded(entry, last)) return [];

    const skips = entry?.skips || {};
    let expected = parseDay(nextOccurrence(last, s.recurrenceType));
    for (let i = 0; skips[dayKey(expected)] && i < 1000; i += 1) {
      expected = parseDay(nextOccurrence(expected, s.recurrenceType));
    }

    const daysLate = daysBetween(expected, start);
    if (daysLate < 0) return [];
    const grace = GRACE_DAYS[s.recurrenceType] ?? GRACE_DAYS.monthly;
    return [{
      key: s.key,
      description: s.description,
      type: s.type,
      amount: round2(s.amount),
      recurrenceType: s.recurrenceType,
      category: latest[s.key]?.category || null,
      expectedDate: expected,
      lastDate: last,
      daysLate,
      prompt: daysLate > grace,
      stale: daysLate > 2 * (CYCLE_DAYS[s.recurrenceType] ?? CYCLE_DAYS.monthly) + grace,
    }];
  });
}

/**
 * Runway events for charges still owed. Outflows only: counting a missed
 * paycheque as arriving tomorrow would lean on money that may never come.
 */
export function missedEvents(missed = []) {
  return missed
    .filter((m) => !m.stale && !isMoneyIn(m.type))
    .map((m) => ({
      date: m.expectedDate,
      seriesKey: m.key,
      description: m.description,
      type: m.type,
      amount: m.amount,
      delta: -m.amount,
      missed: true,
    }));
}

/**
 * Unflagged charges that could be a prompted series' missing one: same
 * direction, near the expected date (or since), a plausible amount.
 */
export function missedCandidates(transactions = [], missed = [], lifecycle = {}, { today = new Date() } = {}) {
  const start = parseDay(today);
  const out = [];
  missed.filter((m) => m.prompt).forEach((m) => {
    const dismissed = lifecycleOf(lifecycle, m.key)?.dismissedTxIds || {};
    const window = LINK_WINDOW_DAYS[m.recurrenceType] ?? LINK_WINDOW_DAYS.monthly;
    transactions
      .map((t) => {
        if (!t || !t.id || t.isRecurring || t.type !== m.type || dismissed[t.id]) return null;
        const d = parseDay(t.date);
        if (!d || d > start || daysBetween(m.expectedDate, d) < -window) return null;
        const ratio = Math.abs(Number(t.amount) || 0) / m.amount;
        if (ratio < LINK_LOW || ratio > LINK_HIGH) return null;
        return { key: m.key, tx: t, offsetDays: Math.abs(daysBetween(m.expectedDate, d)) };
      })
      .filter(Boolean)
      .sort((a, b) => a.offsetDays - b.offsetDays)
      .slice(0, MAX_CANDIDATES)
      .forEach((c) => out.push(c));
  });
  return out;
}

/**
 * Writes that put a charge into a missed series. When its descriptor keys
 * elsewhere, the charge carries the series key explicitly and the descriptor
 * is recorded as an alias, so the next charge under it joins on its own.
 */
export function missedLinkWrites(uid, missed, tx) {
  const base = `users/${uid}`;
  const safe = firebaseSafeKey(missed.key);
  const natural = naturalSeriesKeyOf(tx);
  const updates = {
    [`${base}/transactions/${tx.id}/isRecurring`]: true,
    [`${base}/transactions/${tx.id}/recurrenceType`]: missed.recurrenceType,
  };
  if (natural !== missed.key) {
    updates[`${base}/transactions/${tx.id}/seriesKey`] = missed.key;
    updates[`${base}/seriesLifecycle/${safe}/seriesKey`] = missed.key;
    updates[`${base}/seriesLifecycle/${safe}/aliases/${firebaseSafeKey(natural)}`] = true;
  }
  if (!tx.manualCategory && missed.category) {
    updates[`${base}/transactions/${tx.id}/category`] = missed.category;
  }
  return updates;
}

/** The 'YYYY-MM-DD' a skip or an end is recorded under. */
export const lifecycleDay = (date) => dayKey(parseDay(date));
